import type { FC } from 'react'

import { getConfig } from '@shared/config'
import { minWidthFromTheme } from '@sok/design-system'
import { CtxAddToCart, CtxAddToCartNoStoreSelected } from 'components/CartItemContext'
import { SkeletonLoader } from 'components/SkeletonLoader'
import styled from 'styled-components'

import { AlcoholAlert, NotInCurrentStoreAlert } from './alerts'

const StyledAddToCartContainer = styled.div(({ theme }) => ({
  position: 'fixed',
  width: '100%',
  marginLeft: '-20px',
  bottom: 0,
  padding: '1rem 0',
  backgroundColor: theme.color.white,
  borderTop: `1px solid ${theme.color.customLightGray}`,
  zIndex: 2,

  button: {
    margin: '0 20px',
  },

  [minWidthFromTheme(theme).tablet]: {
    position: 'relative',
    width: '50%',
    maxWidth: '290px',
    border: 0,
  },

  [minWidthFromTheme(theme).desktop]: {
    width: '100%',
    zIndex: 'initial',
  },
}))

interface Props {
  alcoholSellingAllowed?: boolean
  inCurrentStore?: boolean
  isAgeLimitedByAlcohol?: boolean
  isHomeDelivery?: boolean
  hasSelectedStore?: boolean
  loading?: boolean
}

const { domain } = getConfig()

export const AddToCartWithAlerts: FC<Props> = ({
  alcoholSellingAllowed,
  inCurrentStore,
  isAgeLimitedByAlcohol,
  isHomeDelivery,
  hasSelectedStore,
  loading,
}) => {
  const { isSIdEnabled } = domain

  if (loading) {
    return (
      <StyledAddToCartContainer data-test-id="add-to-cart-loading">
        <SkeletonLoader height={40} />
      </StyledAddToCartContainer>
    )
  }

  if (!hasSelectedStore && isSIdEnabled) {
    return (
      <StyledAddToCartContainer>
        <CtxAddToCartNoStoreSelected />
      </StyledAddToCartContainer>
    )
  }

  if (!inCurrentStore && isSIdEnabled) {
    return <NotInCurrentStoreAlert />
  }

  if (isAgeLimitedByAlcohol && isHomeDelivery) {
    return <AlcoholAlert isHomeDelivery />
  }

  if (!alcoholSellingAllowed && isAgeLimitedByAlcohol) {
    return <AlcoholAlert />
  }

  return (
    <>
      <StyledAddToCartContainer>
        <CtxAddToCart />
      </StyledAddToCartContainer>
      {isAgeLimitedByAlcohol && <AlcoholAlert isEnabled />}
    </>
  )
}
